import {
  DataBlockDictionaryDto,
  DataBlockDictionaryItemDto,
  QueryFactory,
  ResourceDto,
} from '@lmt/shared-types';
import { QueryClient, QueryKey } from '@tanstack/react-query';
import React from 'react';
import {
  hardcodedDictionaries,
  hardcodedDictionaryNameById,
  useHardcodedDictionaryItems,
} from '../components/measurements/hardcoded-fields';
import { emptyArray } from '../helpers/common';

export const CustomDictionaryName = {
  InsuranceCompanies: 'dictionaries.insurance_companies._title',
  AbsenceReasons: 'dictionaries.absence_reasons._title',
  PersonTitles: 'dictionaries.person_title._title',
  Sex: 'dictionaries.sex_types._title',
  Titles: 'dictionaries.person_title._title',
  Specialties: 'dictionaries.specialty._title',
  Clinics: 'dictionaries.clinic._title',
  Doctors: 'Doctors',
  ClinicDoctors: 'dictionaries.clinic_doctors._title',
  IgnoredInsuranceCompanyNames:
    'dictionaries.ignored_insurance_company_names._title',
  LevelOfCare: 'dictionaries.level_of_care._title',
};

export function useDictionaryById(
  id: number | null | undefined,
): DataBlockDictionaryDto | undefined | null {
  const dictionariesQuery = QueryFactory.DataBlockDictionaryQuery.useGetQuery();
  const foundDictionary = React.useMemo(
    () => (dictionariesQuery.data ?? []).find((x) => x.id === id),
    [id, dictionariesQuery.data],
  );
  if (!dictionariesQuery.data) {
    return undefined;
  }
  if (!foundDictionary) return null;
  return foundDictionary;
}

export function useDictionariesByIds(
  ids: number[] | null | undefined,
): DataBlockDictionaryDto[] | undefined | null {
  const dictionariesQuery = QueryFactory.DataBlockDictionaryQuery.useGetQuery();
  const foundDictionaries = React.useMemo(
    () => (dictionariesQuery.data ?? []).filter((x) => ids?.includes(x.id)),
    [ids, dictionariesQuery.data],
  );
  if (!dictionariesQuery.data) {
    return undefined;
  }
  if (!foundDictionaries) return null;
  return foundDictionaries;
}

export function useDictionaryByTitle(
  dictionaryTitle: string | undefined | null,
): DataBlockDictionaryDto | undefined | null {
  const dictionariesQuery = QueryFactory.DataBlockDictionaryQuery.useGetQuery({
    enabled: !!dictionaryTitle,
  });
  if (!dictionaryTitle) return undefined;

  if (hardcodedDictionaries[dictionaryTitle]) {
    return hardcodedDictionaries[dictionaryTitle];
  }
  if (!dictionariesQuery.data) {
    return undefined;
  }
  const foundDictionary = dictionariesQuery.data.find(
    (x) => x.title === dictionaryTitle,
  );
  if (!foundDictionary) return null;
  return foundDictionary;
}

export function useDictionaryValue(
  dictionaryTitle: string,
  valueId: number | undefined,
): string | undefined {
  const dictionary = useDictionaryByTitle(dictionaryTitle);
  const dictionaryItemsQuery =
    QueryFactory.DataBlockDictionaryItemQuery.useGetQuery(
      {
        dictionaryId: dictionary?.id,
        all: true,
      },
      {
        enabled: !!dictionary?.id,
      },
    );
  return valueId
    ? dictionaryItemsQuery.data?.data?.find((x) => x.id === valueId)?.value
    : undefined;
}

export function useDictionaryValuesByTitle(
  // try using e.g. `CustomDictionaryName.Sex`
  dictionaryTitle: string,
  searchQuery?: string,
) {
  const dictionary = useDictionaryByTitle(dictionaryTitle);
  return QueryFactory.DataBlockDictionaryItemQuery.useGetQuery(
    {
      dictionaryId: dictionary?.id,
      all: true,
      searchQuery: searchQuery,
    },
    {
      throwOnError: false,
      enabled: !!dictionary?.id,
    },
  );
}

export type DictionaryValuesReturnType = {
  data: DataBlockDictionaryItemDto[];
  isLoading: boolean;
};

export function useDictionaryValuesById(
  dictionaryId: number | null | undefined,
): DictionaryValuesReturnType {
  const query = QueryFactory.DataBlockDictionaryItemQuery.useGetQuery(
    { dictionaryId: dictionaryId ?? 0, all: true },
    {
      enabled: !!dictionaryId && dictionaryId > 0,
    },
  );
  const hardcodedDictionaryItems = useHardcodedDictionaryItems();

  if (!dictionaryId) return { isLoading: false, data: emptyArray() };
  if (dictionaryId < 0) {
    const dictionaryName = hardcodedDictionaryNameById[dictionaryId];
    if (!dictionaryName) return { isLoading: false, data: emptyArray() };
    return {
      isLoading: false,
      data: hardcodedDictionaryItems[dictionaryName] ?? [],
    };
  }

  return { isLoading: query.isLoading, data: query.data?.data ?? emptyArray() };
}

export function useActiveDictionaryValues(
  dictionaryValues: ReturnType<typeof useDictionaryValuesById>,
  selectedValue?: unknown,
): DictionaryValuesReturnType {
  return {
    isLoading: dictionaryValues.isLoading,
    data: dictionaryValues.data.filter(
      (x) =>
        !x.isDeleted ||
        x.id === selectedValue ||
        (Array.isArray(selectedValue) &&
          selectedValue.includes(
            x.id,
          )) /* currently selected value is shown even if deleted */,
    ),
  };
}

export const useActiveDictionaryValuesByTitle = (
  dictionaryTitle: string,
  selectedValue?: unknown,
  searchQuery?: string,
) => {
  const query = useDictionaryValuesByTitle(dictionaryTitle, searchQuery);
  return useActiveDictionaryValues(
    { data: query.data?.data ?? [], isLoading: query.isLoading },
    selectedValue,
  );
};

export const useActiveDictionaryValuesById = (
  dictionaryId: number | null | undefined,
  selectedValue?: unknown,
) =>
  useActiveDictionaryValues(
    useDictionaryValuesById(dictionaryId),
    selectedValue,
  );

export async function getFromCacheOrRequest<T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  queryFunc: (...params: any) => Promise<T>,
): Promise<T> {
  const dataFromCache = queryClient.getQueryData<T>(queryKey);
  const cacheInfo = queryClient.getQueryCache().find({ queryKey });
  if (dataFromCache !== undefined && !cacheInfo?.state?.isInvalidated) {
    return dataFromCache;
  }
  const data = await queryFunc(...queryKey);
  queryClient.setQueryData(queryKey, data);
  return data;
}

export function useResourceDictionary(includeDeleted?: boolean) {
  const resourceQuery = QueryFactory.ResourceQuery.useGetResourcesQuery(
    { includeDeleted: includeDeleted },
    {
      select: (resources) => {
        const result: Record<number, ResourceDto> = {};
        resources.data.forEach((resource) => {
          result[resource.id] = resource;
        });
        return result;
      },
    },
  );
  return resourceQuery;
}
